/**
 * VN30F contract expiry, mirroring the engine's `_contract_expiry_date`
 * (fortuna algorithm/pair/worker/mixins/roll.py): the third Thursday of the
 * month named by the symbol's YYMM suffix.
 *
 * Exists because an expired contract stops quoting, and a frozen Market Price is
 * indistinguishable on screen from a quiet market. That ambiguity cost a
 * debugging session: the pair's deriv leg was still pointed at a contract that
 * had expired the previous day, so the futures leg of the reference PnL could
 * never move and nothing said why.
 *
 * Dates are compared in the APP's local calendar, not UTC — a UTC instant near
 * midnight lands on the wrong day in ICT ([[project-future-frontmonth-roll-tz-bug]]).
 */

/** Third Thursday of `year`/`month` (1-based month), as a local Date at midnight. */
export function thirdThursday(year: number, month: number): Date {
    const first = new Date(year, month - 1, 1);
    // Thursday = 4. Days to add to reach the first Thursday, then two weeks on.
    const offsetToFirstThursday = (4 - first.getDay() + 7) % 7;
    return new Date(year, month - 1, 1 + offsetToFirstThursday + 14);
}

/**
 * Expiry of a VN30F symbol, or null when the suffix is not a YYMM we recognise.
 * Accepts a bare ticker or an exchange-qualified id ("HNXDS:VN30F2608").
 */
export function futureExpiry(symbol: string | null | undefined): Date | null {
    if (!symbol) return null;
    const ticker = symbol.split(':').pop() ?? '';
    const match = ticker.match(/(\d{2})(\d{2})$/);
    if (!match) return null;
    const year = 2000 + Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return null;
    return thirdThursday(year, month);
}

/**
 * Has the contract already expired? Expiry day itself counts as still trading —
 * the engine flattens and rolls at 14:00 on that day, so the contract quotes
 * right up to it ([[project-pair-futures-autoroll]]).
 *
 * @param now injectable for tests; defaults to the app's local clock.
 */
export function isExpiredContract(
    symbol: string | null | undefined,
    now: Date = new Date(),
): boolean {
    const expiry = futureExpiry(symbol);
    if (!expiry) return false;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today.getTime() > expiry.getTime();
}

/** dd/MM/yyyy, for a message an operator reads next to a VN date. */
export function formatExpiry(expiry: Date): string {
    const dd = String(expiry.getDate()).padStart(2, '0');
    const mm = String(expiry.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${expiry.getFullYear()}`;
}

/** The contract that follows `symbol` — what an expired leg should roll to. */
export function nextContract(symbol: string | null | undefined): string | null {
    if (!symbol) return null;
    const ticker = symbol.split(':').pop() ?? '';
    const match = ticker.match(/^(.*?)(\d{2})(\d{2})$/);
    if (!match) return null;
    const [, prefix, yy, mm] = match;
    const month = Number(mm);
    if (month < 1 || month > 12) return null;
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? Number(yy) + 1 : Number(yy);
    return `${prefix}${String(nextYear).padStart(2, '0')}${String(nextMonth).padStart(2, '0')}`;
}

/** Why a manual roll is not offered, or null when it is. */
export type ManualRollBlock =
    | { reason: 'no_pair' }
    | { reason: 'still_trading'; symbol: string; expiry: Date | null };

/**
 * Should the manual ROLL action be offered?
 *
 * Only for a contract that has already EXPIRED. That is the single case the
 * engine cannot handle by itself: its auto-roll fires at 14:00 on expiry day and
 * only while the bot is running, so a pair stopped through that window is left
 * on a dead contract with no other way out.
 *
 * While the contract still trades, rolling is not a fix — it is a way to walk off
 * the liquid front month onto a thin one AND lose the reference price in the
 * process (the roll rewrites it). Two things people reach for the button for are
 * better served elsewhere:
 *   - "the bot is on the wrong contract" → it self-heals at startup and reports
 *     the real one back, so the DB follows;
 *   - "the target price belongs to the old contract" → edit Target Price and
 *     Save; the BE re-stamps it against whatever contract the leg is on now.
 *
 * The BE deliberately does NOT enforce this — an early roll is recoverable
 * (stopped + flat, roll back) and an ops path should not be hard-blocked. This is
 * a guard rail against the accidental second click, not a correctness invariant.
 */
export function manualRollBlock(
    symbol: string | null | undefined,
    hasPair: boolean,
    now: Date = new Date(),
    /**
     * Expiry as the SERVER resolved it (`plan.expiry` from the roll-plan
     * endpoint), ISO `YYYY-MM-DD`. Takes precedence when present: only the server
     * holds the holiday calendar, and a holiday on the third Thursday moves the
     * expiry a day earlier — on that day the local third-Thursday rule would
     * still call a settled contract live and hide the button that fixes it.
     */
    serverExpiryIso?: string | null,
): ManualRollBlock | null {
    if (!hasPair) return { reason: 'no_pair' };
    if (!symbol) return { reason: 'still_trading', symbol: '', expiry: null };
    const expired = serverExpiryIso
        ? localIsoDate(now) > serverExpiryIso
        : isExpiredContract(symbol, now);
    if (!expired) {
        return {
            reason: 'still_trading',
            symbol,
            expiry: serverExpiryIso ? fromIsoDate(serverExpiryIso) : futureExpiry(symbol),
        };
    }
    return null;
}

/** `YYYY-MM-DD` for a local Date, for lexicographic comparison with server dates. */
function localIsoDate(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
}

/** `YYYY-MM-DD` → a local midnight Date, for the existing display helpers. */
function fromIsoDate(iso: string): Date | null {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
